import { useState } from 'react';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@design-system';

import { CcrcTypeAProductCard } from './ccrc-type-a-product-card';
import { useCcrcTypeAProductMetrics } from '../hooks/use-ccrc-type-a-product-metrics';
import { EMPTY_CCRC_TYPE_A_METRICS } from '../lib/ccrc-type-a-metrics';
import { computeCcrcPricing, type CcrcCoverageType } from '../lib/ccrc-type-a-pricing';

interface CcrcTypeAProductOptionsProps {
  products: CcrcTypeAProduct[];
  client: Client;
  partner: Client | null;
  onSelect: (product: CcrcTypeAProduct, coverage: CcrcCoverageType) => void;
}

const COVERAGE_LABELS: Record<CcrcCoverageType, string> = {
  single: 'Single coverage',
  couple: 'Couple coverage',
};

export function CcrcTypeAProductOptions({
  products,
  client,
  partner,
  onSelect,
}: CcrcTypeAProductOptionsProps) {
  const [coverage, setCoverage] = useState<CcrcCoverageType>('single');

  const coverageOptions: CcrcCoverageType[] = partner ? ['single', 'couple'] : ['single'];
  const metricsByProduct = useCcrcTypeAProductMetrics(products, coverage, client, partner);

  return (
    <div className="flex w-full flex-col gap-6">
      <Select
        value={coverage}
        onValueChange={value => setCoverage(value as CcrcCoverageType)}
      >
        <SelectTrigger
          size="sm"
          className="w-max"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {coverageOptions.map(option => (
            <SelectItem
              key={option}
              value={option}
            >
              {COVERAGE_LABELS[option]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {products.map(product => (
          <CcrcTypeAProductCard
            key={product.id}
            product={product}
            pricing={computeCcrcPricing({
              product,
              coverage,
              clientAge: client.intakeSurvey.clientAge,
              partnerAge: partner?.intakeSurvey.clientAge,
              hasPartner: partner != null,
            })}
            metrics={metricsByProduct[product.id] ?? EMPTY_CCRC_TYPE_A_METRICS}
            onSelect={() => onSelect(product, coverage)}
          />
        ))}
      </div>
    </div>
  );
}
